/*!
 * Huedata cookie consent
 * ----------------------
 * Self-contained. No dependencies. Drop in one <script> tag and remove the
 * tracker tags it replaces (see CONSENT-INTEGRATION.md).
 *
 * Design intent: nothing non-essential loads until the visitor opts in.
 * A banner that only *announces* tracking does not satisfy GDPR — the
 * scripts must actually be withheld, which is why the tracker tags come
 * out of the HTML and are injected from here instead.
 */
(function () {
  "use strict";
  
  // The host app evaluates this file twice, so a second copy would paint an
  // identical banner underneath the first — and dismissing one would leave
  // its twin on screen.
  if (window.HuedataConsent) return;

  var STORE_KEY = "huedata_consent";
  var POLICY_VERSION = 1; // bump to re-ask everyone

  // --- the trackers this file gates -------------------------------------
  // IDs taken from the tags currently on huedata.ai.
  var TRACKERS = {
    analytics: [
      { name: "Google Analytics", load: loadGA },
      { name: "Microsoft Clarity", load: loadClarity },
      { name: "Hotjar", load: loadHotjar }
    ],
    functional: [
      { name: "Drift", load: loadDrift }
    ]
  };

  var GA_ID       = "G-B84Z7JJX3Z";
  var CLARITY_ID  = "o1lf04h3n4";
  var HOTJAR_ID   = 5131300;
  var HOTJAR_SV   = 6;
  var DRIFT_ID    = "h4m8bwyzrcht";

  // --- storage -----------------------------------------------------------

  function read() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || v.version !== POLICY_VERSION) return null;
      return v;
    } catch (e) { return null; }
  }

  function write(prefs) {
    var record = {
      version: POLICY_VERSION,
      analytics: !!prefs.analytics,
      functional: !!prefs.functional,
      ts: new Date().toISOString()
    };
    try { localStorage.setItem(STORE_KEY, JSON.stringify(record)); } catch (e) {}
    // Mirror to a cookie so the server can see the choice if it ever needs to.
    try {
      var secure = location.protocol === "https:" ? "; Secure" : "";
      document.cookie = "huedata_consent=" + encodeURIComponent(record.analytics + "," + record.functional) +
        "; path=/; max-age=" + (60 * 60 * 24 * 180) + "; SameSite=Lax" + secure;
    } catch (e) {}
    return record;
  }

  // --- tracker loaders ---------------------------------------------------

  function inject(src, attrs) {
    var s = document.createElement("script");
    s.async = true;
    s.src = src;
    if (attrs) Object.keys(attrs).forEach(function (k) { s.setAttribute(k, attrs[k]); });
    document.head.appendChild(s);
    return s;
  }

  function loadGA() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    inject("https://www.googletagmanager.com/gtag/js?id=" + GA_ID);
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, { anonymize_ip: true });
  }

  function loadClarity() {
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_ID);
  }

  function loadHotjar() {
    window.hj = window.hj || function () { (window.hj.q = window.hj.q || []).push(arguments); };
    window._hjSettings = { hjid: HOTJAR_ID, hjsv: HOTJAR_SV };
    inject("https://static.hotjar.com/c/hotjar-" + HOTJAR_ID + ".js?sv=" + HOTJAR_SV);
  }

  function loadDrift() {
    if (window.drift) return;
    var t = window.driftt = window.drift = window.driftt || [];
    if (t.invoked) return;
    t.invoked = true;
    t.methods = ["identify", "config", "track", "reset", "debug", "show", "ping", "page",
                 "hide", "off", "on"];
    t.factory = function (e) {
      return function () {
        var n = Array.prototype.slice.call(arguments);
        n.unshift(e); t.push(n); return t;
      };
    };
    t.methods.forEach(function (e) { t[e] = t.factory(e); });
    t.load = function (e) {
      inject("https://js.driftt.com/include/" + (Math.ceil(new Date() / 3e5) * 3e5) + "/" + e + ".js");
    };
    t.SNIPPET_VERSION = "0.3.1";
    window.drift.load(DRIFT_ID);
  }

  function applyConsent(prefs) {
    if (prefs.analytics) TRACKERS.analytics.forEach(function (t) { safely(t); });
    if (prefs.functional) TRACKERS.functional.forEach(function (t) { safely(t); });
  }

  function safely(tracker) {
    try { tracker.load(); }
    catch (e) { if (window.console) console.error("[consent] " + tracker.name + " failed to load", e); }
  }

  // Withdrawing consent can't unring the bell for a script already running,
  // so clear what we can and reload to a clean page.
  function purgeAndReload() {
    var doomed = /^(_ga|_gid|_gat|_clck|_clsk|_hj|drift)/i;
    document.cookie.split(";").forEach(function (c) {
      var name = c.split("=")[0].trim();
      if (!doomed.test(name)) return;
      [location.hostname, "." + location.hostname, "." + location.hostname.replace(/^www\./, "")]
        .forEach(function (d) {
          document.cookie = name + "=; path=/; domain=" + d + "; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        });
      document.cookie = name + "=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    });
    location.reload();
  }

  // --- styles ------------------------------------------------------------

  // Design tokens taken from the live site: Aeonik Pro body text, EduFavoritExpanded
  // Bold for uppercase labels, 40px/30px rounded outlined boxes on black, and the
  // #bb0076 magenta with its #ce0b86 hover. The font families are the ones the site
  // already loads; the fallbacks cover pages where they haven't been pulled in.
  var CSS = [
    '.hdc-root{position:fixed;inset:auto 0 0 0;z-index:2147483000;padding:1.25rem;pointer-events:none;',
    'font-family:"Aeonik Pro Regular","Aeonik Pro","Gotham SSm A","Gotham-Book",system-ui,-apple-system,"Segoe UI",Helvetica,Arial,sans-serif}',
    '.hdc-veil{position:fixed;inset:0;background:rgba(0,0,0,.45);opacity:0;transition:opacity .2s ease;pointer-events:none}',
    '.hdc-veil.on{opacity:1;pointer-events:auto}',
    '.hdc-panel{position:relative;pointer-events:auto;max-width:1440px;margin:0 auto;background:#000;color:#fff;',
    'border:2px solid #fff;border-radius:40px;padding:2rem 2.5rem;',
    'display:flex;gap:2rem;align-items:center;justify-content:space-between;flex-wrap:wrap}',
    '.hdc-copy{flex:1 1 26rem;min-width:16rem}',
    '.hdc-title{margin:0 0 .55rem;font-size:.8rem;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.6);',
    'font-family:"EduFavoritExpanded Bold","Aeonik Pro Bold","Gotham SSm A",system-ui,Helvetica,Arial,sans-serif;font-weight:700}',
    '.hdc-text{margin:0;font-size:1rem;line-height:1.6;color:rgba(255,255,255,.9);max-width:64ch}',
    '.hdc-text a{color:#fff;text-decoration:underline;text-underline-offset:3px}',
    '.hdc-actions{display:flex;gap:.75rem;align-items:center;flex-wrap:wrap}',
    '.hdc-btn{font-family:"EduFavoritExpanded Bold","Aeonik Pro Bold","Gotham SSm A",system-ui,Helvetica,Arial,sans-serif;font-weight:700;font-size:.8125rem;',
    'letter-spacing:.06em;text-transform:uppercase;min-width:175px;padding:.9rem 1.75rem;border-radius:999px;',
    'cursor:pointer;border:1.5px solid transparent;transition:.2s ease;white-space:nowrap}',
    '.hdc-btn:focus-visible{outline:2px solid #fff;outline-offset:3px}',
    '.hdc-accept{background:#bb0076;color:#fff;border-color:#bb0076}',
    '.hdc-accept:hover{background:#ce0b86;border-color:#ce0b86}',
    '.hdc-reject{background:transparent;color:#fff;border-color:#fff}',
    '.hdc-reject:hover{background:#fff;color:#000}',
    '.hdc-link{background:none;border:none;color:rgba(255,255,255,.7);text-decoration:underline;',
    'text-underline-offset:4px;font-family:inherit;font-size:.9375rem;letter-spacing:.01em;text-transform:none;',
    'padding:.5rem .25rem;cursor:pointer}',
    '.hdc-link:hover{color:#fff}',
    /* preferences dialog */
    '.hdc-modal{position:fixed;inset:0;z-index:2147483001;display:flex;align-items:center;justify-content:center;padding:1.25rem;',
    'font-family:"Aeonik Pro Regular","Aeonik Pro","Gotham SSm A","Gotham-Book",system-ui,-apple-system,"Segoe UI",Helvetica,Arial,sans-serif}',
    '.hdc-card{position:relative;width:100%;max-width:37rem;max-height:88vh;overflow:auto;background:#000;color:#fff;',
    'border:2px solid #fff;border-radius:40px;padding:2.5rem}',
    '.hdc-card:focus{outline:none}',
    '.hdc-card h2{margin:0 0 .6rem;font-size:1.5rem;line-height:1.2;font-weight:400;letter-spacing:-.01em;',
    'font-family:"Aeonik Pro Bold","Aeonik Pro Regular","Gotham SSm A",system-ui,Helvetica,Arial,sans-serif}',
    '.hdc-card>p{margin:0 0 1.5rem;font-size:.9375rem;line-height:1.6;color:rgba(255,255,255,.7)}',
    '.hdc-cat{border:1.5px solid rgba(255,255,255,.35);border-radius:20px;padding:1.25rem 1.5rem;margin-bottom:.75rem;',
    'display:flex;gap:1.25rem;align-items:flex-start;justify-content:space-between}',
    '.hdc-cat h3{margin:0 0 .35rem;font-size:1rem;font-weight:400;font-family:"Aeonik Pro Bold","Aeonik Pro Regular","Gotham SSm A",system-ui,Helvetica,Arial,sans-serif}',
    '.hdc-cat p{margin:0;font-size:.875rem;line-height:1.55;color:rgba(255,255,255,.68)}',
    '.hdc-cat .hdc-vendors{margin-top:.5rem;font-size:.75rem;letter-spacing:.08em;text-transform:uppercase;',
    'font-family:"EduFavoritExpanded Bold","Aeonik Pro Bold","Gotham SSm A",system-ui,Helvetica,Arial,sans-serif;color:rgba(255,255,255,.45)}',
    /* toggle: the site's 48x28 outlined pill with a 20px knob inset 4px */
    '.hdc-sw{position:relative;flex:0 0 auto;width:48px;height:28px;margin-top:.1rem}',
    '.hdc-sw input{position:absolute;opacity:0;width:100%;height:100%;margin:0;cursor:pointer}',
    '.hdc-sw span{position:absolute;inset:0;background:transparent;border:1.5px solid rgba(255,255,255,.55);',
    'border-radius:999px;transition:.2s;pointer-events:none}',
    '.hdc-sw span::after{content:"";position:absolute;top:2.5px;left:2.5px;width:20px;height:20px;background:transparent;',
    'border:1.5px solid rgba(255,255,255,.55);border-radius:50%;transition:.2s}',
    '.hdc-sw input:checked+span{background:#bb0076;border-color:#bb0076}',
    '.hdc-sw input:checked+span::after{background:#fff;border-color:#fff;transform:translateX(20px)}',
    '.hdc-sw input:disabled+span{border-color:rgba(255,255,255,.25);background:rgba(255,255,255,.12)}',
    '.hdc-sw input:disabled+span::after{background:rgba(255,255,255,.5);border-color:transparent;transform:translateX(20px)}',
    '.hdc-sw input:focus-visible+span{outline:2px solid #fff;outline-offset:3px}',
    '.hdc-foot{display:flex;gap:.75rem;justify-content:flex-end;flex-wrap:wrap;padding-top:1.5rem}',
    '@media (max-width:640px){.hdc-root{padding:.75rem}',
    '.hdc-panel{flex-direction:column;align-items:stretch;gap:1.25rem;border-radius:30px;padding:1.5rem}',
    '.hdc-copy{flex:0 0 auto;min-width:0}.hdc-text{font-size:.9375rem}.hdc-actions{width:100%;gap:.5rem}',
    '.hdc-btn{flex:1 1 auto;min-width:0;text-align:center;padding:.9rem 1rem}',
    '.hdc-link{order:1;flex:1 0 100%;text-align:center}',
    '.hdc-card{border-radius:30px;padding:1.75rem}.hdc-cat{padding:1.1rem 1.25rem}',
    '.hdc-foot .hdc-btn{flex:1 1 auto}}',
    '@media (prefers-reduced-motion:reduce){.hdc-veil,.hdc-sw span,.hdc-sw span::after,.hdc-btn{transition:none}}'
  ].join("");

  function styles() {
    if (document.getElementById("hdc-styles")) return;
    var s = document.createElement("style");
    s.id = "hdc-styles";
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  // --- UI ----------------------------------------------------------------

  var root = null, lastFocus = null;

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function closeAll() {
    if (root) { root.remove(); root = null; }
    var m = document.getElementById("hdc-modal");
    if (m) m.remove();
    document.body.style.overflow = "";
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
  }

  function banner() {
    styles();
    root = el("div", "hdc-root");
    root.setAttribute("role", "region");
    root.setAttribute("aria-label", "Cookie consent");

    var panel = el("div", "hdc-panel");
    var copy = el("div", "hdc-copy");
    copy.appendChild(el("p", "hdc-title", "Cookies"));
    copy.appendChild(el("p", "hdc-text",
      'We use cookies and similar technologies that are necessary for the site to work. With your ' +
      'permission we would also like to use analytics cookies — including tools that record how pages ' +
      'are used — and a live chat widget. Nothing optional loads until you choose. ' +
      '<a href="/#!/privacy-policy">Privacy policy</a>.'));

    var actions = el("div", "hdc-actions");
    var prefs  = el("button", "hdc-link", "Manage preferences");
    var reject = el("button", "hdc-btn hdc-reject", "Reject all");
    var accept = el("button", "hdc-btn hdc-accept", "Accept all");

    prefs.type = reject.type = accept.type = "button";
    prefs.onclick  = function () { openPreferences(); };
    reject.onclick = function () { applyConsent(write({ analytics: false, functional: false })); closeAll(); };
    accept.onclick = function () { applyConsent(write({ analytics: true,  functional: true  })); closeAll(); };

    actions.appendChild(prefs);
    actions.appendChild(reject);
    actions.appendChild(accept);
    panel.appendChild(copy);
    panel.appendChild(actions);
    root.appendChild(panel);
    document.body.appendChild(root);
  }

  function categoryRow(id, title, desc, vendors, checked, locked) {
    var row = el("div", "hdc-cat");
    var left = el("div");
    left.appendChild(el("h3", null, title));
    left.appendChild(el("p", null, desc));
    left.appendChild(el("p", "hdc-vendors", vendors));

    var sw = el("label", "hdc-sw");
    var input = document.createElement("input");
    input.type = "checkbox";
    input.id = id;
    input.checked = checked;
    if (locked) input.disabled = true;
    input.setAttribute("aria-label", title);
    sw.appendChild(input);
    sw.appendChild(el("span"));

    row.appendChild(left);
    row.appendChild(sw);
    return row;
  }

  function openPreferences() {
    styles();
    lastFocus = document.activeElement;
    var existing = read() || { analytics: false, functional: false };

    var wrap = el("div", "hdc-modal");
    wrap.id = "hdc-modal";
    var veil = el("div", "hdc-veil on");
    veil.onclick = function () { closePreferences(); };

    var card = el("div", "hdc-card");
    card.setAttribute("role", "dialog");
    card.setAttribute("aria-modal", "true");
    card.setAttribute("aria-labelledby", "hdc-h");

    var h = el("h2", null, "Cookie preferences");
    h.id = "hdc-h";
    card.appendChild(h);
    card.appendChild(el("p", null,
      "Choose what Huedata may load. Rejecting leaves only what the site needs to work. You can change this at any time from the link in the footer."));

    card.appendChild(categoryRow("hdc-nec", "Strictly necessary",
      "Required for the site to function — sign-in, security, and payment processing. These cannot be turned off.",
      "Stripe · session cookies", true, true));

    card.appendChild(categoryRow("hdc-ana", "Analytics",
      "Helps us understand how the site is used. Includes tools that record page interactions and replay sessions.",
      "Google Analytics · Microsoft Clarity · Hotjar", existing.analytics, false));

    card.appendChild(categoryRow("hdc-fun", "Functional",
      "Powers the live chat widget so you can reach us from any page.",
      "Drift", existing.functional, false));

    var foot = el("div", "hdc-foot");
    var rejectAll = el("button", "hdc-btn hdc-reject", "Reject all");
    var save      = el("button", "hdc-btn hdc-accept", "Save preferences");
    rejectAll.type = save.type = "button";

    rejectAll.onclick = function () { commit({ analytics: false, functional: false }); };
    save.onclick = function () {
      commit({
        analytics:  document.getElementById("hdc-ana").checked,
        functional: document.getElementById("hdc-fun").checked
      });
    };

    foot.appendChild(rejectAll);
    foot.appendChild(save);
    card.appendChild(foot);

    wrap.appendChild(veil);
    wrap.appendChild(card);
    document.body.appendChild(wrap);
    document.body.style.overflow = "hidden";

    wrap.addEventListener("keydown", onKey);
    // Focus the dialog itself, not a button: the title is announced first and the
    // card opens scrolled to the top on short screens.
    card.tabIndex = -1;
    setTimeout(function () { try { card.focus(); card.scrollTop = 0; } catch (e) { save.focus(); } }, 0);
  }

  function onKey(ev) {
    if (ev.key === "Escape") { closePreferences(); return; }
    if (ev.key !== "Tab") return;
    var card = document.querySelector(".hdc-card");
    if (!card) return;
    var f = card.querySelectorAll('button, input:not([disabled]), a[href]');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
    else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
  }

  function closePreferences() {
    var m = document.getElementById("hdc-modal");
    if (m) m.remove();
    document.body.style.overflow = "";
    if (!read() && !root) banner(); // no decision yet — bring the banner back
  }

  function commit(prefs) {
    var had = read();
    var rec = write(prefs);
    closeAll();
    // Turning something off that was previously on: only a reload truly stops it.
    var revoked = had && ((had.analytics && !rec.analytics) || (had.functional && !rec.functional));
    if (revoked) { purgeAndReload(); return; }
    applyConsent(rec);
  }

  // --- boot --------------------------------------------------------------

  function start() {
    var saved = read();

    if (saved) { applyConsent(saved); return; }

    // Honour an explicit browser-level opt-out without asking.
    var gpc = navigator.globalPrivacyControl === true;
    var dnt = navigator.doNotTrack === "1" || window.doNotTrack === "1";
    if (gpc || dnt) { write({ analytics: false, functional: false }); return; }

    banner();
  }

  window.HuedataConsent = {
    open: openPreferences,          // wire a footer link to this
    get: read,
    reset: function () {
      try { localStorage.removeItem(STORE_KEY); } catch (e) {}
      purgeAndReload();
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
